import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Database, RefreshCw, Users } from "lucide-react";
import AdminCard from "./db/AdminCard";
import { RecordList, UserList } from "./db/DbLists";
import { api, errMsg } from "../../lib/api";

const NavItem = ({ active, onClick, icon: Icon, label, count, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full flex items-center gap-2 px-3 h-8 rounded-md text-xs font-mono transition-colors ${active ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}>
    <Icon size={12} className="text-emerald-600/80" /><span className="truncate flex-1 text-left">{label}</span><span className="text-slate-400">{count}</span>
  </button>
);

export const DatabasePane = ({ project }) => {
  const [summary, setSummary] = useState(null);
  const [sel, setSel] = useState("__users");
  const [rows, setRows] = useState(null);

  const pick = (name) => { if (name !== sel) { setRows(null); setSel(name); } };

  const loadSummary = useCallback(() => api.dbSummary(project.id).then(setSummary).catch((e) => toast.error(errMsg(e))), [project.id]);
  const loadRows = useCallback(async () => {
    setRows(null);
    try { setRows(sel === "__users" ? await api.dbUsers(project.id) : await api.dbRecords(project.id, sel)); } catch (e) { toast.error(errMsg(e)); setRows([]); }
  }, [project.id, sel]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const refresh = () => { loadSummary(); loadRows(); };
  const removeRecord = async (r) => {
    try { await api.dbDeleteRecord(project.id, sel, r.id); toast.success("Record deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const removeUser = async (u) => {
    try { await api.dbDeleteUser(project.id, u.id); toast.success(`Removed ${u.email}`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="h-full flex" data-testid="database-pane">
      <aside className="w-56 shrink-0 border-r border-slate-200 bg-slate-50/80 flex flex-col">
        <div className="px-3 py-2.5 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-slate-500">
          App data <button onClick={refresh} data-testid="db-refresh-btn" aria-label="Refresh" className="hover:text-slate-900"><RefreshCw size={12} /></button>
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll px-2 space-y-0.5">
          <NavItem active={sel === "__users"} onClick={() => pick("__users")} icon={Users} label="Accounts" count={summary?.users ?? ""} testid="db-nav-users" />
          {(summary?.collections || []).map((c) => (
            <NavItem key={c.name} active={sel === c.name} onClick={() => pick(c.name)} icon={Database} label={c.name} count={c.count} testid={`db-nav-${c.name}`} />
          ))}
          {summary && !summary.collections.length && <p className="px-3 py-2 text-[11px] text-slate-400" data-testid="db-no-collections">No saved data yet.</p>}
        </div>
      </aside>
      <div className="flex-1 min-w-0 overflow-y-auto thin-scroll p-4 sm:p-6 space-y-5">
        <AdminCard projectId={project.id} />
        <h3 className="text-sm font-semibold text-slate-900" data-testid="db-section-title">{sel === "__users" ? "Accounts in your app" : `Collection · ${sel}`}</h3>
        {sel === "__users" ? <UserList rows={rows} onRemove={removeUser} /> : <RecordList rows={rows} onRemove={removeRecord} />}
      </div>
    </div>
  );
};

export default DatabasePane;
